import { Dialog } from '@weapp/ui';
import { isEbRuntimeApp } from '@weapp/utils';
import {
  BlockPathList,
  BuildLib,
  FixedMenu,
  MultiFill,
  PageModuleType,
  SystemMenu,
  SystemMenuList,
} from '../constants';
import ebdcoms from '../utils/ebdcoms';
import { dialogManager } from './dialogs/utils';
import { ModalConfigType } from './types';

// 配置弹框类型字段
const dlgRegExp = /\/[A-Za-z]+dlg\//;

// 获取右侧滑出弹窗的top高度
export const getDialogTopFormLeft = () => {
  const url = location.href;

  let top: string | number = 0;

  // 设计页面预览
  const isDesignPreView = /ebddesigner\/preview/.test(url);
  // 应用预览
  const isAppPreView = /ebdapp\/preview/.test(url);
  // 应用运行 - PC
  const isAppView = /ebdapp\/view/.test(url);
  // 单页面运行 - pc
  const isPageView = /ebdpage/.test(url);
  // 绘图页面预览
  const isScenePreview = /(scene|sp\/scene)\/preview/.test(url);
  // 绘图页面运行
  const isSceneRender = /scene\/render/.test(url);
  // 绘图页面运行
  const isSceneRenderSp = /sp\/scene\/render/.test(url);
  // bi仪表盘预览
  const isBiDashBoradPreView = /sp\/analyze\/bi\/dashboard\/preview/.test(url);

  if (isDesignPreView || isScenePreview || isBiDashBoradPreView) {
    top = '45px';
  } else if (isAppPreView || (isSceneRender && !isSceneRenderSp)) {
    top = '50px';
  } else if (isAppView || isPageView) {
    top = 0; // 0为不设置
  } else if (isSceneRenderSp) {
    top = '0px';
  }

  return top;
};

/**
 * 根据路由判断是否放行
 * @param pathName 路由
 * @returns 判断结果
 * - true --> 阻挡
 * - false --> 放行
 */
const getPathBlockStatus = (pathName: string) => {
  if (!pathName || typeof pathName !== 'string') {
    return false;
  }

  // 系统菜单允许放行
  if (SystemMenuList.find((sys) => pathName?.toLowerCase().includes(sys?.toLowerCase()))) {
    return false;
  }

  // 预期的路由格式是 /xxxx
  const [, pathNameStr] = pathName?.split('/') || [];

  const isBlocked = BlockPathList?.some((item) => item === pathNameStr);

  if (pathNameStr && isBlocked) {
    return true;
  }

  return false;
};

const routeUtils = {
  getDialogProps(pageType: string, placement: string, winSize?: string, isBlankClose = true) {
    // const url = location.href;
    // 主框架下面不用加top，用主框架的弹窗默认高度
    // const isLayout = /\/sp/.test(url);

    const dialogProps: any = {
      width: '',
      height: '',
      top: '',
      closable: true,
      destroyOnClose: true,
      noMaskClose: isBlankClose,
      maskClosable: isBlankClose,
      scale: true,
    };

    const dlgTop = getDialogTopFormLeft();

    const dialogTop = Dialog.dialogConfig?.top || 0;

    switch (pageType) {
      case 'PageDialog':
        dialogProps.width = placement === 'middle' ? '60%' : winSize || '800px';
        dialogProps.height = placement === 'middle' ? '60%' : `calc(100% - ${dlgTop})`;
        dialogProps.top = placement === 'middle' ? '100px' : dlgTop;
        break;
      case 'LinkDialog':
        dialogProps.width = placement === 'middle' ? '80%' : winSize || '800px';
        dialogProps.height = placement === 'middle' ? '80%' : `calc(100% - ${dlgTop})`;
        dialogProps.top = placement === 'middle' ? '100px' : dlgTop;
        break;
      case 'WriteDaily':
      case 'LayoutDialog':
      case 'SearchDialog':
        dialogProps.width = placement === 'middle' ? '800px' : winSize || '800px';
        dialogProps.height = dlgTop ? `calc(100% - ${dlgTop})` : `calc(100% - ${dialogTop}px)`;

        if (placement === 'middle') {
          dialogProps.height = `${document.body.clientHeight - 60}px`;
        }

        dialogProps.top = placement === 'middle' ? '30px' : dlgTop;
        break;
      default:
        break;
    }

    // if (isLayout) {
    //   delete dialogProps.top;
    // }

    return dialogProps;
  },
  // 针对表单卡片配置二级弹框情况
  // 获取二级弹框关闭后路由
  // 门户系统弹框关闭
  getBackPath(url: string, pathname: string, type?: PageModuleType) {
    let path = url;

    if ((/\/card\//.test(pathname) && /dlg\//.test(pathname)) || type === PageModuleType.Portal) {
      const dialogType = pathname.match(dlgRegExp)?.[0];

      if (dialogType) {
        [path] = pathname.split(dialogType);
      }
    }

    return path;
  },
  // 获取页面type 和 id 信息
  getPageInfo(pathname: string, excludeSetting?: boolean) {
    const isBlockedPath = getPathBlockStatus(pathname);
    if (isBlockedPath === true) {
      return ['', ''];
    }
    let type = '';
    let id = '';

    const { PageType } = ebdcoms.get();
    const excludeSettingMenu = [...SystemMenuList, PageType.Combinate, FixedMenu.Setting];
    if (excludeSetting) {
      excludeSettingMenu.pop();
    }
    let isbuildPage = false;

    if (/\/ebdapp\/build/.test(location.pathname)) {
      isbuildPage = true;
    }

    const fragments = pathname.split('/');
    if (fragments?.length > 1) {
      fragments.some((item: string, index: number) => {
        // 排除最后一个选项，会无法取id
        if (index > fragments.length - 2) {
          return true;
        }
        const supportPageTypes = [
          PageType.Page,
          PageType.ViewPort,
          PageType.Search,
          PageType.Workflow,
          PageType.Report,
          PageType.Link,
          PageType.SystemPage,
          PageType.Layout,
          PageType.Scene,
          FixedMenu.Base,
          FixedMenu.Setting,
          MultiFill.MultireportMonitor,
          MultiFill.MultireportTask,
          BuildLib.Template,
          BuildLib.Clause,
          PageType.Combinate,
          ...SystemMenuList,
          PageType.BussComp,
        ].map((pageType) => pageType?.toLowerCase());
        const itemType = item.toLowerCase();
        const sceneType = PageType.Scene?.toLowerCase();

        // 处理report/view/xx 路由，判断pageId 来确认是否命中report/xx 路由规则
        if (itemType === PageType.Report.toLowerCase()) {
          const _reportId = fragments[index + 1];
          const reportId = _reportId?.includes('-') ? _reportId?.split('-')[0] : _reportId;

          if (!/^\d+$/.test(reportId)) return false;
        }

        if (supportPageTypes.includes(itemType)) {
          if (
            (itemType === sceneType && !['render', 'preview'].includes(fragments[index + 1]))
            || itemType !== sceneType
          ) {
            // eb预览运行页-当前布局追加url：pageId取第一个page后面的id，不再向后匹配
            if (type === 'page') {
              if (isbuildPage) {
                isbuildPage = false;
              } else {
                return true;
              }
            }

            type = item;

            // 选择连接-系统菜单：pageId固定为 cusapp_combination/{module}/xxx
            if (itemType.toUpperCase() === SystemMenu.System) {
              const secondRoute = fragments[index + 2] ? `/${fragments[index + 2]}` : '';
              const thirdRoute = fragments[index + 3] ? `/${fragments[index + 3]}` : '';
              id = `${fragments[index + 1]}${secondRoute}${thirdRoute}`;
            } else {
              const _id = fragments[index + 1];
              id = this.formatterPageId(_id?.includes('-') ? _id?.split('-')[0] : _id);
            }
            // 系统菜单匹配之后不再向后匹配
            // 设置菜单匹配后不再往后匹配
            // 组合页面匹配后不再往后匹配
            if (excludeSettingMenu.map((sys) => sys?.toLowerCase()).includes(itemType)) {
              return true;
            }
          }
        }

        return false;
      });
    }

    return [type, id];
  },
  /**
   * 处理独立部署路由调整后 pageId 拼入应用ID的问题
   * - 独立部署应用对应的路由: appId_pageId
   */
  formatterPageId: (pageId: string) => {
    if (!pageId) {
      return pageId;
    }

    // 预期是纯数字，避免影响其他包含 _ 的路由
    if (/^\d+_\d+$/.test(pageId)) {
      const [appId = '', _pageId = ''] = pageId.split('_') || [];

      // 只调整独立部署应用的
      if (appId && _pageId && isEbRuntimeApp?.(appId)) {
        return _pageId;
      }
    }

    return pageId;
  },
};

const dialogRouteLayoutZIndex = 998;

/** modal弹窗统一参数 */
export const getModalConfigParams = (params: ModalConfigType) => {
  const {
    placement, isBlankClose, modalHeight, modalWidth, dialogInstance,
  } = params;
  const p: any = {
    maskClosable: !!isBlankClose,
    noMaskClose: !!isBlankClose,
    top: undefined,
  };

  if (placement === 'middle') {
    if (modalHeight !== undefined && modalHeight !== '') {
      p.height = modalHeight;
      p.dialogParam = {
        ...p.dialogParam,
        height: modalHeight,
        top: undefined,
      };
    }
    if (modalWidth !== undefined && modalWidth !== '') {
      p.width = modalWidth;
      p.dialogParam = {
        ...p.dialogParam,
        width: modalWidth,
      };
    }
  }

  if (placement === 'right') {
    delete p.top;
  }

  // 多个路由 弹窗或者侧滑弹窗打开，控制 mask样式
  if (dialogManager.index >= 0) {
    const dialogInstanceInd = dialogManager.getIndex(dialogInstance);
    const isOpenMiddle = placement === 'middle';

    if (dialogInstanceInd === 0) return p;

    return {
      ...p,
      mask: isOpenMiddle || dialogInstanceInd > 0 || dialogInstanceInd === -1,
      zIndex: dialogRouteLayoutZIndex,
    };
  }

  return p;
};

export default routeUtils;
